/**
 * Simplified Admin API Service
 * Working version with current API service
 */
import api from './api';
import { SessionSettings } from '../types/auth';
import { DEBUG_LOGS } from '../utils/debugFlags';

// Basic types
export interface SystemHealth {
  cpu_usage: number;
  memory_usage: number;
  disk_usage: number;
  database_connections: number;
  active_users: number;
  websocket_connections: number;
  message_throughput: number;
  error_rate: number;
  response_time: number;
  last_updated: string;
  alerts_count: number;
  critical_alerts: number;
}

export interface SystemStats {
  total_users: number;
  active_users_today: number;
  total_messages: number;
  messages_today: number;
  total_hospitals: number;
  total_departments: number;
  total_units: number;
  ai_delegations_today: number;
  failed_logins_today: number;
  system_alerts: number;
  bulk_operations_pending: number;
  audit_logs_today: number;
}

export interface AIServiceStatus {
  emergency_shutdown: boolean;
  global_enabled: boolean;
  ai_provider_api_enabled: boolean;
  disabled_roles: string[];
  disabled_users_count: number;
  active_sessions_count: number;
}

export interface AIControlResponse {
  success: boolean;
  message?: string;
  reason?: string;
}

/**
 * Admin API Service Class
 */
class AdminApiService {
  private baseUrl = '/admin';
  private apiBaseUrl = '';

  // Generic HTTP Methods
  async get<T = any>(endpoint: string): Promise<T> {
    return await api.get<T>(endpoint);
  }

  async post<T = any>(endpoint: string, data?: any): Promise<T> {
    return await api.post<T>(endpoint, data);
  }

  async patch<T = any>(endpoint: string, data?: any): Promise<T> {
    return await api.patch<T>(endpoint, data);
  }

  async put<T = any>(endpoint: string, data?: any): Promise<T> {
    return await api.put<T>(endpoint, data);
  }

  async delete<T = any>(endpoint: string): Promise<T> {
    return await api.delete<T>(endpoint);
  }

  // Authentication & Session (use auth/session-status/ so cookie auth is applied; /admin/session-status/ does not)
  async checkSessionStatus(): Promise<any> {
    const data = await api.get<any>('/auth/session-status/', { _isAuthCheck: true } as any);
    if (!data) return data;
    return {
      is_authenticated: data.authenticated === true,
      is_admin: data.user?.is_admin === true,
      username: data.user?.username ?? null,
      session_id: data.session?.id ?? data.session_id ?? null,
    };
  }

  // System Health & Stats
  async getSystemHealth(): Promise<SystemHealth> {
    return await api.get<SystemHealth>(`${this.baseUrl}/system/health/`);
  }

  async getSystemStats(): Promise<SystemStats> {
    return await api.get<SystemStats>(`${this.baseUrl}/system/stats/`);
  }

  // Hospital Management APIs
  async getHospitals(): Promise<any> {
    return await api.get<any>(`/hospital-config/`);
  }

  async getDepartments(hospitalIds?: number[]): Promise<any> {
    let url = '/departments/';
    if (hospitalIds && hospitalIds.length > 0) {
      const params = hospitalIds.map(id => `hospital_ids=${id}`).join('&');
      url = `${url}?${params}`;
    }
    if (DEBUG_LOGS) {
      console.log('[adminApi.getDepartments] Calling URL:', url, 'with hospitalIds:', hospitalIds);
    }
    try {
      const result = await api.get<any>(url);
      if (DEBUG_LOGS) {
        console.log('[adminApi.getDepartments] Response:', { 
          type: typeof result, 
          isArray: Array.isArray(result), 
          keys: result ? Object.keys(result) : 'null', 
          length: Array.isArray(result) ? result.length : (result?.results?.length || 0),
          sample: Array.isArray(result) ? result.slice(0, 2) : (result?.results?.slice(0, 2) || null)
        });
      }
      return result;
    } catch (error: any) {
      console.error('[adminApi.getDepartments] Error:', error);
      throw error;
    }
  }

  async getUnits(hospitalIds?: number[]): Promise<any> {
    let url = '/units/';
    if (hospitalIds && hospitalIds.length > 0) {
      const params = hospitalIds.map(id => `hospital_ids=${id}`).join('&');
      url = `${url}?${params}`;
    }
    if (DEBUG_LOGS) console.log('[adminApi.getUnits] Calling URL:', url, 'with hospitalIds:', hospitalIds);
    try {
      const result = await api.get<any>(url);
      if (DEBUG_LOGS) console.log('[adminApi.getUnits] Response:', { 
        type: typeof result, 
        isArray: Array.isArray(result), 
        keys: result ? Object.keys(result) : 'null', 
        length: Array.isArray(result) ? result.length : (result?.results?.length || 0),
        sample: Array.isArray(result) ? result.slice(0, 2) : (result?.results?.slice(0, 2) || null)
      });
      return result;
    } catch (error: any) {
      console.error('[adminApi.getUnits] Error:', error);
      throw error;
    }
  }

  async getRoleSpecialties(hospitalIds?: number[]): Promise<any> {
    let url = '/role-specialties/';
    if (hospitalIds && hospitalIds.length > 0) {
      const params = hospitalIds.map(id => `hospital_ids=${id}`).join('&');
      url = `${url}?${params}`;
    }
    if (DEBUG_LOGS) console.log('[adminApi.getRoleSpecialties] Calling URL:', url, 'with hospitalIds:', hospitalIds);
    try {
      const result = await api.get<any>(url);
      if (DEBUG_LOGS) console.log('[adminApi.getRoleSpecialties] Response:', { 
        type: typeof result, 
        isArray: Array.isArray(result), 
        keys: result ? Object.keys(result) : 'null', 
        length: Array.isArray(result) ? result.length : (result?.results?.length || 0),
        sample: Array.isArray(result) ? result.slice(0, 2) : (result?.results?.slice(0, 2) || null)
      });
      return result;
    } catch (error: any) {
      console.error('[adminApi.getRoleSpecialties] Error:', error);
      throw error;
    }
  }

  // Security & Audit APIs
  async getAuditLogs(params?: any): Promise<any> {
    const queryParams = params ? `?${new URLSearchParams(params).toString()}` : '';
    return await api.get<any>(`${this.baseUrl}/audit-logs/${queryParams}`);
  }

  async getSecurityEvents(params?: any): Promise<any> {
    const queryParams = params ? `?${new URLSearchParams(params).toString()}` : '';
    return await api.get<any>(`/audit/security-events/${queryParams}`);
  }

  async getAuditSummary(): Promise<any> {
    return await api.get<any>(`/audit/summary/`);
  }

  // Alerts & Notifications APIs
  async getAlerts(params?: any): Promise<any> {
    const queryParams = params ? `?${new URLSearchParams(params).toString()}` : '';
    return await api.get<any>(`${this.baseUrl}/alerts/${queryParams}`);
  }

  async resolveAlert(alertId: number): Promise<any> {
    return await api.post<any>(`${this.baseUrl}/alerts/${alertId}/resolve/`);
  }

  async createAlert(alertData: any): Promise<any> {
    return await api.post<any>(`${this.baseUrl}/alerts/`, alertData);
  }

  async updateAlert(alertId: number, alertData: any): Promise<any> {
    return await api.put<any>(`${this.baseUrl}/alerts/${alertId}/`, alertData);
  }

  async deleteAlert(alertId: number): Promise<any> {
    return await api.delete<any>(`${this.baseUrl}/alerts/${alertId}/`);
  }

  // Session Policy API (hospital-scoped)
  async getSessionSettings(hospitalId: number): Promise<SessionSettings> {
    return await api.get<SessionSettings>(`/session-policy/${hospitalId}/`);
  }

  async updateSessionSettings(hospitalId: number, data: Partial<SessionSettings>): Promise<SessionSettings> {
    return await api.patch<SessionSettings>(`/session-policy/${hospitalId}/`, data);
  }

  // System Configuration APIs
  async getSystemConfigurations(params?: any): Promise<any> {
    const queryParams = params ? `?${new URLSearchParams(params).toString()}` : '';
    return await api.get<any>(`${this.baseUrl}/system-config/${queryParams}`);
  }

  // PATCH: callers send only the fields they change (e.g. { value }); a PUT
  // would demand the whole row. Platform-wide rows - superuser only server-side.
  async updateSystemConfiguration(configId: number, configData: any): Promise<any> {
    return await api.patch<any>(`${this.baseUrl}/system-config/${configId}/`, configData);
  }

  async createSystemConfiguration(configData: any): Promise<any> {
    return await api.post<any>(`${this.baseUrl}/system-config/`, configData);
  }

  // User Management APIs
  async getUsers(params?: any): Promise<any> {
    const queryParams = params ? `?${new URLSearchParams(params).toString()}` : '';
    return await api.get<any>(`${this.baseUrl}/users/${queryParams}`);
  }

  async getUser(userId: number): Promise<any> {
    return await api.get<any>(`${this.baseUrl}/users/${userId}/`);
  }

  async createUser(userData: any): Promise<any> {
    return await api.post<any>(`${this.baseUrl}/users/`, userData);
  }

  async updateUser(userId: number, userData: any): Promise<any> {
    return await api.put<any>(`${this.baseUrl}/users/${userId}/`, userData);
  }

  async deleteUser(userId: number): Promise<any> {
    return await api.delete<any>(`${this.baseUrl}/users/${userId}/`);
  }

  async bulkUserAction(action: string, userIds: number[], data?: any): Promise<any> {
    return await api.post<any>(`${this.baseUrl}/users/bulk-action/`, {
      action,
      user_ids: userIds,
      ...data
    });
  }

  // Dashboard Summary
  async getDashboardSummary(): Promise<{
    systemHealth: SystemHealth;
    systemStats: SystemStats;
    recentAlerts: any[];
    pendingOperations: any[];
  }> {
    try {
      const [systemHealth, systemStats] = await Promise.all([
        this.getSystemHealth(),
        this.getSystemStats(),
      ]);

      return {
        systemHealth,
        systemStats,
        recentAlerts: [], // TODO: Implement when alerts endpoint is ready
        pendingOperations: [], // TODO: Implement when operations endpoint is ready
      };
    } catch (error) {
      if (DEBUG_LOGS) {
        console.error('Failed to fetch dashboard summary:', error);
      }
      throw error;
    }
  }

  // Urgency Permission Management APIs
  async getUrgencyPermissions(params?: any): Promise<any> {
    const queryParams = params ? `?${new URLSearchParams(params).toString()}` : '';
    return await api.get<any>(`${this.baseUrl}/urgency-permissions/${queryParams}`);
  }

  async getUserUrgencyPermissions(userId: number): Promise<any> {
    return await api.get<any>(`${this.baseUrl}/urgency-permissions/${userId}/`);
  }

  async updateUserUrgencyPermission(permissionId: number, data: any): Promise<any> {
    if (DEBUG_LOGS) {
      console.log('[adminApi] updateUserUrgencyPermission called:', {
        permissionId,
        data: JSON.parse(JSON.stringify(data)),
        hospital_id: data.hospital_id,
        hospital_id_type: typeof data.hospital_id
      });
    }
    try {
      const response = await api.patch<any>(`${this.baseUrl}/urgency-permissions/${permissionId}/`, data);
      if (DEBUG_LOGS) console.log('[adminApi] updateUserUrgencyPermission success:', response);
      return response;
    } catch (error: any) {
      console.error('[adminApi] updateUserUrgencyPermission error:', {
        error,
        response: error.response?.data,
        status: error.response?.status
      });
      throw error;
    }
  }

  async createUserUrgencyPermission(data: any): Promise<any> {
    if (DEBUG_LOGS) {
      console.log('[adminApi] createUserUrgencyPermission called with data:', JSON.parse(JSON.stringify(data)));
      console.log('[adminApi] Data breakdown:', {
        user: data.user,
        user_type: typeof data.user,
        hospital_id: data.hospital_id,
        hospital_id_type: typeof data.hospital_id,
        other_fields: Object.keys(data).filter(k => !['user', 'hospital_id'].includes(k))
      });
    }
    try {
      const response = await api.post<any>(`${this.baseUrl}/urgency-permissions/`, data);
      if (DEBUG_LOGS) console.log('[adminApi] createUserUrgencyPermission success:', response);
      return response;
    } catch (error: any) {
      console.error('[adminApi] createUserUrgencyPermission error:', {
        error,
        response: error.response?.data,
        status: error.response?.status
      });
      throw error;
    }
  }

  async deleteUserUrgencyPermission(permissionId: number): Promise<any> {
    return await api.delete<any>(`${this.baseUrl}/urgency-permissions/${permissionId}/`);
  }

  async getUrgencyPermissionMatrix(params?: any): Promise<any> {
    const queryParams = params ? `?${new URLSearchParams(params).toString()}` : '';
    return await api.get<any>(`${this.baseUrl}/urgency-permissions/matrix/${queryParams}`);
  }

  async bulkUpdateUrgencyPermissions(data: any): Promise<any> {
    if (DEBUG_LOGS) {
      console.log('[adminApi] bulkUpdateUrgencyPermissions called:', {
        user_ids: data.user_ids,
        user_count: data.user_ids?.length || 0,
        permission_updates: data.permission_updates,
        hospital_id: data.hospital_id,
        reason: data.reason
      });
    }
    
    try {
      const response = await api.post<any>(`${this.baseUrl}/urgency-permissions/bulk-update/`, data);
      if (DEBUG_LOGS) console.log('[adminApi] bulkUpdateUrgencyPermissions success:', response);
      return response;
    } catch (error: any) {
      console.error('[adminApi] bulkUpdateUrgencyPermissions error:', {
        error,
        response: error.response?.data,
        status: error.response?.status
      });
      throw error;
    }
  }

  async getRoleUrgencyTemplates(): Promise<any> {
    return await api.get<any>(`${this.baseUrl}/urgency-templates/`);
  }

  async applyRoleTemplate(templateId: number, userIds: number[]): Promise<any> {
    return await api.post<any>(`${this.baseUrl}/urgency-templates/${templateId}/apply/`, {
      user_ids: userIds
    });
  }

  // Enhanced Urgency Permission APIs
  async getEffectivePermissions(userId: number): Promise<any> {
    return await api.get<any>(`${this.baseUrl}/urgency-permissions/users/${userId}/effective/`);
  }

  async validatePermissionChanges(userId: number, changes: any): Promise<any> {
    return await api.post<any>(`${this.baseUrl}/urgency-permissions/validate-changes/`, {
      user_id: userId,
      changes
    });
  }

  async simulatePermissionChanges(userId: number, changes: any): Promise<any> {
    return await api.post<any>(`${this.baseUrl}/urgency-permissions/simulate-changes/`, {
      user_id: userId,
      changes
    });
  }

  async enhancedBulkUpdate(operations: any[], reason?: string, executeImmediately = true, reviewRequired = false): Promise<any> {
    return await api.post<any>(`${this.baseUrl}/urgency-permissions/enhanced-bulk-update/`, {
      operations,
      reason: reason || 'Bulk operation',
      execute_immediately: executeImmediately,
      review_required: reviewRequired
    });
  }

  async validateUrgencyPermission(urgencyLevel: string, userId?: number): Promise<any> {
    const data: any = { urgency_level: urgencyLevel };
    if (userId) {
      data.user_id = userId;
    }
    return await api.post<any>(`${this.baseUrl}/urgency-permissions/validate/`, data);
  }

  // AI Service Control APIs
  async getAIServiceStatus(): Promise<{ data: AIServiceStatus }> {
    return await api.get<{ data: AIServiceStatus }>(`/ai/control/status/`);
  }

  async setGlobalAIStatus(enabled: boolean): Promise<AIControlResponse> {
    return await api.post<AIControlResponse>(`/ai/control/global_toggle/`, { enabled });
  }

  async setRoleAIStatus(role: string, enabled: boolean): Promise<AIControlResponse> {
    return await api.post<AIControlResponse>(`/ai/control/role_control/`, { role, enabled });
  }

  async setUserAIStatus(userId: number, enabled: boolean): Promise<AIControlResponse> {
    return await api.post<AIControlResponse>(`/ai/control/user_control/`, { user_id: userId, enabled });
  }

  async emergencyShutdown(adminKey1: string, adminKey2: string, reason: string): Promise<AIControlResponse> {
    return await api.post<AIControlResponse>(`/ai/control/emergency_shutdown/`, {
      admin_key_1: adminKey1,
      admin_key_2: adminKey2,
      reason
    });
  }

  async reactivateAIServices(adminKey1: string, adminKey2: string): Promise<AIControlResponse> {
    return await api.post<AIControlResponse>(`/ai/control/reactivate/`, {
      admin_key_1: adminKey1,
      admin_key_2: adminKey2
    });
  }

  // NOTE (2026-08-23): the three methods below target actions that do NOT exist
  // on AIServiceControlViewSet (backend/ai_permissions/views.py defines only
  // status, global_toggle, role_control, user_control, emergency_shutdown,
  // reactivate, superuser_emergency_shutdown, superuser_reactivate). They 404
  // even with the prefix corrected, and nothing in the app calls them. Left in
  // place rather than deleted so the gap is visible; wire up a backend action
  // before using them.
  async getAIControlLogs(): Promise<any> {
    return await api.get<any>(`/ai/control/control_logs/`);
  }

  async getUserAIPermissions(userId?: number): Promise<any> {
    const params = userId ? `?user_id=${userId}` : '';
    return await api.get<any>(`/ai/control/user_permissions/${params}`);
  }

  async updateUserAIPermissions(userId: number, permissions: any): Promise<any> {
    return await api.post<any>(`/ai/control/update_user_permissions/`, {
      user_id: userId,
      ...permissions
    });
  }

  async superuserEmergencyShutdown(reason: string): Promise<AIControlResponse> {
    return await api.post<AIControlResponse>(`/ai/control/superuser_emergency_shutdown/`, {
      reason
    });
  }

  async superuserReactivateAI(): Promise<AIControlResponse> {
    return await api.post<AIControlResponse>(`/ai/control/superuser_reactivate/`);
  }

  // Patient Experience Management APIs (Mock implementation for testing)
  async getPatientExperienceRoles(): Promise<any> {
    // Mock data for testing
    await new Promise(resolve => setTimeout(resolve, 300)); // Simulate API delay
    return {
      data: [
        {
          id: 1,
          name: 'Patient Experience Manager',
          code: 'pe_mgr',
          description: 'Manages overall patient experience and handles escalations',
          active_users: 2,
          permissions: ['View All Feedback', 'Assign Tasks', 'View Analytics', 'Manage Settings']
        },
        {
          id: 2,
          name: 'Patient Feedback Specialist',
          code: 'pe_feed',
          description: 'Specializes in processing and responding to general patient feedback',
          active_users: 5,
          permissions: ['View Patient Feedback', 'Respond to Feedback', 'Route Messages']
        },
        {
          id: 3,
          name: 'Complaint Resolution Specialist',
          code: 'pe_comp',
          description: 'Handles formal complaints and complex resolution processes',
          active_users: 3,
          permissions: ['View Complaints', 'Create Resolutions', 'Escalate Issues', 'Legal Documentation']
        },
        {
          id: 4,
          name: 'Patient Satisfaction Analyst',
          code: 'pe_sat',
          description: 'Analyzes satisfaction data and generates insights',
          active_users: 1,
          permissions: ['View Analytics', 'Generate Reports', 'Survey Management', 'Data Analysis']
        }
      ]
    };
  }

  async getPatientExperienceUserAssignments(): Promise<any> {
    await new Promise(resolve => setTimeout(resolve, 400));
    return {
      data: [
        {
          id: 1,
          user: {
            id: 101,
            username: 'pe_manager1',
            full_name: 'Sarah Johnson',
            email: 'sarah.johnson@hospital.com'
          },
          role_specialty: {
            id: 1,
            name: 'Patient Experience Manager',
            code: 'pe_mgr'
          },
          is_active: true,
          current_workload: 8,
          assigned_at: '2024-01-15T10:00:00Z'
        },
        {
          id: 2,
          user: {
            id: 102,
            username: 'pe_specialist1',
            full_name: 'Michael Chen',
            email: 'michael.chen@hospital.com'
          },
          role_specialty: {
            id: 2,
            name: 'Patient Feedback Specialist',
            code: 'pe_feed'
          },
          is_active: true,
          current_workload: 12,
          assigned_at: '2024-02-01T14:30:00Z'
        },
        {
          id: 3,
          user: {
            id: 103,
            username: 'pe_complaint1',
            full_name: 'Dr. Emma Rodriguez',
            email: 'emma.rodriguez@hospital.com'
          },
          role_specialty: {
            id: 3,
            name: 'Complaint Resolution Specialist',
            code: 'pe_comp'
          },
          is_active: true,
          current_workload: 6,
          assigned_at: '2024-01-20T09:15:00Z'
        }
      ]
    };
  }

  async getPatientExperienceRoutingRules(): Promise<any> {
    await new Promise(resolve => setTimeout(resolve, 350));
    return {
      data: [
        {
          id: 1,
          name: 'Billing & Insurance Issues',
          content_keywords: ['billing', 'insurance', 'bill', 'payment', 'charge', 'cost'],
          priority: 1,
          assigned_role: 'Patient Experience Manager',
          is_active: true,
          match_count: 147
        },
        {
          id: 2,
          name: 'Complaints & Dissatisfaction',
          content_keywords: ['complaint', 'dissatisfied', 'unhappy', 'terrible', 'awful', 'poor service'],
          priority: 2,
          assigned_role: 'Complaint Resolution Specialist',
          is_active: true,
          match_count: 89
        },
        {
          id: 3,
          name: 'General Feedback',
          content_keywords: ['feedback', 'suggestion', 'comment', 'experience', 'opinion'],
          priority: 3,
          assigned_role: 'Patient Feedback Specialist',
          is_active: true,
          match_count: 234
        },
        {
          id: 4,
          name: 'Satisfaction Surveys',
          content_keywords: ['survey', 'rating', 'satisfaction', 'recommend', 'score'],
          priority: 4,
          assigned_role: 'Patient Satisfaction Analyst',
          is_active: false,
          match_count: 56
        }
      ]
    };
  }

  async getPatientExperienceSettings(hospitalId: number): Promise<any> {
    const params = new URLSearchParams({ hospital_id: hospitalId.toString() });
    return api.get(`/patient/admin/hospital-feedback-settings/?${params.toString()}`);
  }

  async updatePatientExperienceSettings(hospitalId: number, settings: any): Promise<any> {
    const params = new URLSearchParams({ hospital_id: hospitalId.toString() });
    return api.patch(`/patient/admin/hospital-feedback-settings/?${params.toString()}`, settings);
  }

  async getPatientExperienceAnalytics(): Promise<any> {
    await new Promise(resolve => setTimeout(resolve, 600));
    return {
      data: {
        active_specialists: 11,
        total_feedback: 1247,
        feedback_this_week: 89,
        feedback_this_month: 342,
        avg_response_time_hours: 4.2,
        resolution_rate: 87.3,
        satisfaction_score: 4.1,
        by_role: [
          {
            role: 'Patient Experience Manager',
            count: 147,
            avg_time: 2.8
          },
          {
            role: 'Patient Feedback Specialist', 
            count: 234,
            avg_time: 3.1
          },
          {
            role: 'Complaint Resolution Specialist',
            count: 89,
            avg_time: 6.4
          },
          {
            role: 'Patient Satisfaction Analyst',
            count: 56,
            avg_time: 1.9
          }
        ],
        trending_issues: [
          {
            issue: 'Billing Questions',
            count: 147,
            trend: 'up'
          },
          {
            issue: 'Wait Times',
            count: 98,
            trend: 'down'
          },
          {
            issue: 'Staff Courtesy',
            count: 76,
            trend: 'up'
          },
          {
            issue: 'Appointment Scheduling',
            count: 54,
            trend: 'stable'
          }
        ]
      }
    };
  }

  async togglePatientExperienceRole(roleId: number): Promise<any> {
    await new Promise(resolve => setTimeout(resolve, 300));
    if (DEBUG_LOGS) console.log('Toggling role status for role ID:', roleId);
    return {
      success: true,
      message: 'Role status updated successfully'
    };
  }

  async togglePatientExperienceRoutingRule(ruleId: number): Promise<any> {
    await new Promise(resolve => setTimeout(resolve, 250));
    if (DEBUG_LOGS) console.log('Toggling routing rule for rule ID:', ruleId);
    return {
      success: true,
      message: 'Routing rule updated successfully'
    };
  }

  async updatePatientExperienceUserAssignment(userId: number, assignmentData: any): Promise<any> {
    await new Promise(resolve => setTimeout(resolve, 400));
    if (DEBUG_LOGS) console.log('Updating user assignment for user ID:', userId, 'with data:', assignmentData);
    return {
      success: true,
      message: 'User assignment updated successfully'
    };
  }

  async exportUsersCSV(params?: any): Promise<Blob> {
    return api.downloadBlob('/admin/users/export-csv/', params);
  }
}

// Export singleton instance
export const adminApi = new AdminApiService();
export default adminApi;
